<template>
<file-layout
    :file-name="fileName"
    :show-header="isPublic"
    :can-download="isPublic"
    :allow-share="false"
  >
    <div id="editor"></div>
  </file-layout>
</template>

<script>
import cookies from "js-cookie";
// import { TocaEditor } from "@toca/toca-edit";
// import "@toca/toca-edit/dist/style.css";
import FileLayout from "@/components/h5/Layout";
import { EDITOR_ENV_MAP, BATCH_AUTH_DETAIL_LIST } from "@/constants/file";
import { mapState, mapActions } from "vuex";
import { AGENT_ID, WEB_PATH } from "@/constants/auth";
import { goLogin } from "@/utils/auth";
import {
  saviorEventUUID,
  setSaviorConfigure,
  updateSaviorData,
} from "../utils/savior";
import { debounce } from "@/utils/lodash";
import { navigatorLang } from "@/utils/lang";
import { getEditorWidthSetting } from "@/api/h5/file";
import * as Sentry from "@sentry/vue";

export default {
  props: {
    rootId: {
      type: String,
    },
    fileId: {
      type: String,
    },
    metadata: {
      type: Object,
    },
    isPublic: {
      type: Boolean,
    },
    editorMode: {
      type: String,
      default: "PREVIEW",
    },
    isLogin: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isMettingDoc: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FileLayout,
  },
  data() {
    return {
      editor: null,
    };
  },
  methods: {
    ...mapActions(["setTocaEditor"]),
    formatDuration(duration){
        if(duration <= 2000){
            return `2s`;
        }else if(duration <= 3000){
            return `3s`;
        }else if(duration <= 4000){
            return `4s`;
        }else if(duration <= 5000){
            return `5s`;
        }else{
            return '5s+';
        }
    },
    async initEditor(editorMode) {
      const userId = cookies.get("userId") || "";
      const token = cookies.get("token") || "";
      const deviceId = cookies.get("deviceId") || "";
      const spaceId = cookies.get("spaceId") || "";

      const userInfo = {
        userId,
        token,
        deviceId,
        spaceId,
        userDetail: this.currentUserInfo,
        ...this.currentUserInfo,
        ...this.userInfo,
      };
      let env = EDITOR_ENV_MAP[process.env.VUE_APP_STATIC_NODE_ENV];

      // 异步加载模块
      const TocaEditor = await import("@toca/toca-edit")
        .then((module) => module.TocaEditor)
        .catch(() => {});
      import("@toca/toca-edit/dist/style.css");

      let isCanEdit = this.isPcMode ? editorMode : false;
      if (this.isEditorPreview && this.isEditorPreview.length > 0) {
        isCanEdit = this.isEditorPreview === "true" ? false : true;
      }

      let isPreview =
        this.isEditorPreview &&
        this.isEditorPreview.length > 0 &&
        this.isEditorPreview === "true";

      this.editor = Object.freeze(new TocaEditor("#editor", {
        fileId: this.fileId,
        isCanEdit: isCanEdit,
        isCanCopy: this.isCanCopy,
        isPreview: isPreview,
        isMettingDoc: this.isMettingDoc,
        env,
        showDirectory: true,
        showVisitRecord: this.showVisitRecord,
        showtype: this.showtype,
        userInfo: this.isLogin ? userInfo : {},
        userData: this.isLogin ? userInfo : null,
        docInfo: this.docInfo,
        onTitleChange: (data) => {
          this.docInfo.fileName = data;
          this.$emit("handleChangeWikiTitle", data);
        },
        onNoPermission: () => {
          goLogin(window.location.href, AGENT_ID.file);
        },
        commentToLogin: () => {
          goLogin(window.location.href, AGENT_ID.file);
          // authorize({
          //   redirectUrl: window.location.href,
          //   agentId: AGENT_ID.file,
          //   BASE_URL: process.env.VUE_APP_BASE_URL
          // })
        },
        onSelectionUpdate: (content) => {
          this.$emit("onSelectionUpdate", content);
        },
        onChangeEditStatus: (isCanEdit) => {
          this.$emit("onChangeEditStatus", isCanEdit);
        },
        markdownSettingsCode: "FILE.ENABLED_MARKDOWN_PLUGIN",
        savior: {
          saviorEventUUID,
          setSaviorConfigure,
          updateSaviorData,
        },
        locale:
          this.cloudI18n ||
          localStorage.getItem("tci18nLocale") ||
          navigatorLang[navigator.language] ||
          "zh-cn",
      }));
      this.editor.editor.on('synced', () => {
            try {
                const { tocaEditorLoadStartTransaction } = window.tocaSentryService;
                if (!tocaEditorLoadStartTransaction) return;
                const { href } = tocaEditorLoadStartTransaction.getSpanJSON().data || {};
                if (href === window.location.href) {
                    if(window.tocaSentryStartTimeStamp && window.tocaSentryStartTimeStamp > 0 && !this.showtype){
                        const startupDuration = Date.now() - window.tocaSentryStartTimeStamp;
                        const startupStr = this.formatDuration(startupDuration);
                        Sentry.captureEvent({
                            message: "App Startup Time",
                            level: "info",
                            tags: {
                                event_type: "startup",
                                startupDuration:startupStr
                            } 
                        });
                    }
                    tocaEditorLoadStartTransaction?.end();
                    window.tocaSentryService.tocaEditorLoadStartTransaction = null;
                }
            } catch (error) {
                console.log('统计首屏加载时间出错', error);
            }
      })
      this.setTocaEditor(this.editor);
      this.editor.setIsReadOnly(this.readOnly);

      if(this.isLogin){
        this.requestAndSetEditorWidth();
      }
    },
    updateDocInfo(data) {
      this.editor?.updateDocInfo(data);
    },
    requestAndSetEditorWidth(){
        getEditorWidthSetting(this.fileId).then((res) => {
            if (res.code == 200) {
                const selectMode = res.data.settingsValue;
                setTimeout(() => {
                    if (selectMode == "FIXED") {
                        this.updateEditorWidth(false);
                    } else {
                        this.updateEditorWidth(true);
                    }
                }, 1000);
            }
        });
    },
    updateEditorWidth(value) {
      this.editor?.setEditFullWidth(value);
    },
    save(fn) {
      this.editor?.saveDocument(
        debounce((result) => {
          console.log("-----------------result:", result);
          fn();
        }, 1000)
      );
    },
    saveKbDoc() {
      this.editor?.saveKbDoc();
    },
    setIsCanEdit(value) {
      this.editor?.setIsCanEdit(value);
    },
    setShowVisitRecord(value) {
      this.editor?.setShowVisitRecord(value);
    },
    requestFullscreen() {
      this.editor?.requestFullScreen();
    },
  },
  computed: {
    ...mapState({
      isPcMode: (state) => state.isPcMode,
      currentUserInfo: (state) => state.currentUserInfo,
      docInfo: (state) => state.docInfo,
      cloudI18n: (state) => state.cloudI18n,
      showtype: (state) => state.showType,
      userInfo: (state) => state.userInfo, //兼容考培系统用户信息
      showVisitRecord: (state) =>
        state.wikiSpaceInfo?.wikiSpaceSettings?.showVisitRecord,
      isCanCopy: (state) => {
        const { auth } = state.docInfo;
        // ALL一定能够复制
        if (auth === 'ALL') return true;

        return state.isCanCopy;
      },
    }),
    isEditorPreview() {
      const isPreview = new URLSearchParams(window.location.search).get(
        "isPreview"
      );
      return isPreview;
    },
    fileName() {
      return this.metadata ? this.metadata?.fileName : "";
    },
  },
  watch: {
    editorMode(v) {
      this.editor?.setIsCanEdit(v !== "PREVIEW");
      // this.editor = null;
      // this.initEditor(v !== 'PREVIEW');
    },
    showVisitRecord(v) {
      this.editor?.setShowVisitRecord(v);
    },
    readOnly: {
      handler(val) {
        this.editor?.setIsReadOnly(val);
        if (!!this.readOnly && this.readOnly === true) {
          // 兼容组织人才移动端readonly不能让其编辑逻辑
          setTimeout(() => {
            this.editor?.setIsCanEdit(false);
            this.editor?.setIsReadOnly(true);
          }, 1500);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initEditor(this.editorMode !== "PREVIEW");
  },
  destroyed() {
    this.editor?.destroy();
    this.editor = null;
    this.setTocaEditor(null);
  },
};
</script>

<style lang="scss">
#editor {
  max-width: 100%;
  max-height: 100%;
  width: 100% !important;
  height: 100% !important;
  background-color: #ffffff;
}
</style>
